import { MutableRefObject, useCallback, useMemo, useRef, useState } from "react";
import {
  ColumnFiltersState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import type { Table as TableType } from "@tanstack/table-core";
import { OnChangeFn, PaginationState, TableOptions, VisibilityState } from "@tanstack/table-core";
import { genericMemo } from "../../utils";
import { EmptyPlaceholder } from "./Body/EmptyPlaceholder";
import { Body } from "./Body";
import { BulkActions } from "./BulkActions";
import { TableContextProvider } from "./context";
import { ControlBar, SettingsSidebar } from "./ControlBar";
import { RowSelectionExt, ServerSideRowSelection } from "./features";
import { Header } from "./Header";
import { Pagination } from "./Pagination";
import { TableProps } from "./types";
import { defaultPrettifyFilterOperatorLabel, useSetupColumns, useTableData } from "./utils";

import styles from "./styles.module.scss";

const TableContainer = genericMemo(<TData extends object, TPredefinedFilter extends unknown = unknown>(tableProps: TableProps<TData, TPredefinedFilter>) => {
  const {
    data: originalData,
    fetchData,
    columns: originalColumns,
    loadStrategy,
    displayStrategy,
    fetchingDebounceTimeout,
    defaultSorting,
    defaultFilters,
    defaultPagination,
    defaultColumnVisibility,
    defaultColumnOrder,
    defaultGlobalFilter,
    enableGlobalFilter,
    defaultPredefinedFilter,
    applyPredefinedFilter,
    modifyTableOptions,
    modifyColumns,
    getRowId,
    isFetching: externalIsFetching
  } = tableProps;

  const tableRef = useRef<TableType<TData>>(null) as MutableRefObject<TableType<TData>>;
  const [ sorting, setSorting ] = useState<SortingState>(defaultSorting!);
  const [ pagination, setPagination ] = useState<PaginationState>(defaultPagination!);
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>(defaultFilters!);
  const [ columnVisibility, setColumnVisibility ] = useState<VisibilityState>(defaultColumnVisibility!);
  const [ columnOrder, setColumnOrder ] = useState<string[]>(defaultColumnOrder!);
  const [ globalFilter, setGlobalFilter ] = useState<string>(defaultGlobalFilter!);
  const [ predefinedFilter, setPredefinedFilter ] = useState<TPredefinedFilter | undefined>(defaultPredefinedFilter);
  const [ rowSelection, setRowSelection ] = useState<RowSelectionState>({});

  const setupColumns = useSetupColumns(originalColumns);
  const columns = useMemo(
    () => modifyColumns?.(setupColumns) || setupColumns,
    [ setupColumns, modifyColumns ],
  );
  const { isFetching, data, rowCount, pageCount } = useTableData<TData, TPredefinedFilter>({
    data: originalData,
    fetchData,
    debounceTimeout: fetchingDebounceTimeout,
    pagination,
    sorting,
    filters: columnFilters,
    tableRef,
    globalFilter,
    predefinedFilter,
    applyPredefinedFilter,
  });

  const options: TableOptions<TData> = {
    _features: loadStrategy === "server" ? [ ServerSideRowSelection, RowSelectionExt ] : [ RowSelectionExt ],
    getRowId,
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    rowCount,
    pageCount,
    initialState: {
      pagination: defaultPagination,
      sorting: defaultSorting,
      columnFilters: defaultFilters,
      columnVisibility: defaultColumnVisibility,
      columnOrder: defaultColumnOrder,
      globalFilter: defaultGlobalFilter,
    },
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      columnOrder,
      globalFilter,
      rowSelection,
    },
    meta: {
      predefinedFilter: predefinedFilter || null,
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnFiltersChange: useCallback<OnChangeFn<ColumnFiltersState>>((updaterOrValue) => {
      if (loadStrategy === "server") {
        setPagination(prevState => ({
          ...prevState,
          pageIndex: 0,
        }));
      }
      setColumnFilters(updaterOrValue);
    }, [ loadStrategy ]),
    onGlobalFilterChange: setGlobalFilter,
    enableGlobalFilter,
    globalFilterFn: loadStrategy === "client" ? "auto" : undefined,
    columnResizeMode: "onChange",
    onRowSelectionChange: setRowSelection,
  };

  if (loadStrategy === "server") {
    options.manualPagination = true;
    options.manualSorting = true;
    options.manualFiltering = true;
  } else if (loadStrategy === "client") {
    options.getSortedRowModel = getSortedRowModel();
    options.getFilteredRowModel = getFilteredRowModel();
  }

  if (displayStrategy === "pagination") {
    options.getPaginationRowModel = getPaginationRowModel();
    options.onPaginationChange = setPagination;
    options.state = {
      ...options.state,
      pagination,
    };
  }

  const table = useReactTable(modifyTableOptions?.(options) ?? options);
  tableRef.current = table;

  return (
    <TableContextProvider
      table={table}
      tableProps={tableProps}
    >
      <div className={styles.root}>
        <Header />
        <ControlBar
          setPredefinedFilter={setPredefinedFilter}
        />
        <Body isFetching={externalIsFetching ?? isFetching} />
        <BulkActions />
        <Pagination />
        <SettingsSidebar />
      </div>
    </TableContextProvider>
  );
});

export const Table = genericMemo(<TData extends object, TPredefinedFilter extends unknown = unknown>(
  {
    loadStrategy = "client",
    displayStrategy = "pagination",
    fetchingDebounceTimeout = 0,
    defaultPageSize = 10,
    defaultSorting = [],
    defaultFilters = [],
    defaultPagination = {
      pageIndex: 0,
      pageSize: defaultPageSize,
    },
    pageOptions = [ 5, 10, 20, 30, 40, 50, 100 ],
    defaultColumnVisibility = {},
    defaultColumnOrder = [],
    pinnedFilters = [],
    applyPredefinedFilter = () => {throw new Error("applyPredefinedFilter not provided!");},
    controlBarView = "regular",
    prettifyFilterOperatorLabel = defaultPrettifyFilterOperatorLabel,
    getAllTableData = (table) => table.getPrePaginationRowModel()
      .rows
      .map(x => x.original),
    emptyPlaceholderComponent = EmptyPlaceholder,
    modifyColumns = columns => columns,
    ...restProps
  }: TableProps<TData, TPredefinedFilter>) => {

  return (
    <TableContainer
      key={`${loadStrategy}_${displayStrategy}_${defaultPageSize}`}
      loadStrategy={loadStrategy}
      displayStrategy={displayStrategy}
      fetchingDebounceTimeout={fetchingDebounceTimeout}
      defaultPageSize={defaultPageSize}
      defaultSorting={defaultSorting}
      defaultFilters={defaultFilters}
      defaultPagination={defaultPagination}
      defaultColumnVisibility={defaultColumnVisibility}
      defaultColumnOrder={defaultColumnOrder}
      pageOptions={pageOptions}
      applyPredefinedFilter={applyPredefinedFilter}
      controlBarView={controlBarView}
      pinnedFilters={pinnedFilters}
      prettifyFilterOperatorLabel={prettifyFilterOperatorLabel}
      getAllTableData={getAllTableData}
      emptyPlaceholderComponent={emptyPlaceholderComponent}
      modifyColumns={modifyColumns}
      useVirtualizeColumns
      enableGlobalFilter
      {...restProps}
    />
  );
});
